import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Award, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { masterclassesAPI } from "../services/api";
import { staticMasterclasses } from "../data/staticConferenceData";
import Modal from "../components/Modal";
import "./Masterclasses.css";

const Masterclasses = () => {
  const { eventId } = useParams();
  const [masterclasses, setMasterclasses] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // State for active tab
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchMasterclasses();
    fetchMyRegistrations();
  }, [eventId]);

  const fetchMasterclasses = async () => {
    setLoading(true);
    try {
      const response = await masterclassesAPI.getAll(eventId);
      setMasterclasses(response.data);
    } catch (error) {
      console.error("Failed to fetch masterclasses:", error);
      // Fallback to static conference data if API fails
      setMasterclasses(staticMasterclasses);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRegistrations = async () => {
    try {
      const response = await masterclassesAPI.getMyRegistrations();
      // Ensure we are mapping correctly based on response structure
      const ids = new Set(response.data.map((m) => m.masterclass_id || m.id));
      setMyRegistrations(ids);
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), "h:mm a");
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "EEE, MMM d");
    } catch {
      return dateString;
    }
  };

  // Group masterclasses by fuzzy start_time to define rounds
  const rounds = masterclasses.reduce((acc, mc) => {
    const date = new Date(mc.start_time);
    // Round to nearest 30 minutes to handle minor entry differences (like 9:15 vs 9:17)
    const minutes = date.getMinutes();
    const roundedMinutes = Math.floor(minutes / 30) * 30;
    date.setMinutes(roundedMinutes);
    date.setSeconds(0);
    date.setMilliseconds(0);
    
    // Create a stable key for grouping (Year-Month-Day Hour:Minute)
    const timeKey = date.toISOString();
    
    if (!acc[timeKey]) {
      acc[timeKey] = [];
    }
    acc[timeKey].push(mc);
    return acc;
  }, {});

  // Sort rounds by time
  const sortedRoundTimes = Object.keys(rounds).sort();

  // Create round objects
  const roundTabs = sortedRoundTimes.map((time, index) => {
    const firstClass = rounds[time][0];
    const dateStr = formatDate(firstClass.start_time);
    const startTime = formatTime(firstClass.start_time);
    const endTime = formatTime(firstClass.end_time);

    return {
      id: index,
      label: `Round ${index + 1}`,
      time: `${dateStr} • ${startTime} – ${endTime}`,
      classes: rounds[time],
    };
  });

  const currentRoundClasses = roundTabs[activeTab]?.classes || [];

  const getAvailability = (mc) => {
    // Backend API uses 'current_registrations', demo uses 'registered'
    const registeredCount =
      mc.current_registrations !== undefined
        ? mc.current_registrations
        : mc.registered;
    const available = mc.capacity - registeredCount;

    if (available <= 0)
      return { status: "full", text: "Full", color: "var(--rf-error)" };
    if (available <= 5)
      return {
        status: "limited",
        text: `${available} left`,
        color: "var(--rf-warning)",
      };
    return {
      status: "available",
      text: `${available} available`,
      color: "var(--rf-success)",
    };
  };

  const isRegistered = (mcId) => myRegistrations.has(mcId);

  // === FIX: Helper functions to handle API (Object) vs Demo (String) data ===
  const getInstructorNames = (instructors) => {
    if (!instructors || instructors.length === 0) return "TBD";
    return instructors.map((ins) => ins.name).join(", ");
  };

  const getInstructorTitles = (mc) => {
    if (mc.instructors && mc.instructors.length > 0) {
      return mc.instructors
        .map((ins) => ins.title)
        .filter(Boolean)
        .join(", ");
    }
    // Demo data might have it on root
    return mc.instructor_title || "";
  };

  // Error Modal State
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  // Registration loading state
  const [registeringId, setRegisteringId] = useState(null);

  const handleRegisterClick = async (e, mc) => {
    e.stopPropagation();

    // Prevent multiple clicks
    if (registeringId) return;

    setRegisteringId(mc.id);

    try {
      const response = await masterclassesAPI.register(mc.id);

      if (response.data.success || response.data.status) {
        // Update local state to reflect registration
        setMyRegistrations((prev) => new Set(prev).add(mc.id));

        // Optimistic UI update is enough. We don't want to trigger
        // the global loading state by calling fetchMasterclasses().
      }
    } catch (error) {
      console.error("Registration failed:", error);

      // Check for specific error message from backend
      const errorMessage =
        error.response?.data?.detail || "Failed to register. Please try again.";

      // Show custom modal instead of system alert
      setErrorModal({ isOpen: true, message: errorMessage });
    } finally {
      setRegisteringId(null);
    }
  };

  if (loading) {
    return (
      <div className="masterclasses-page">
        <div className="masterclass-info-banner">
          <Award size={20} />
          <span>Masterclasses have limited seats. Register early!</span>
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="masterclass-skeleton">
            <div
              className="skeleton"
              style={{ height: 24, width: "80%", marginBottom: 8 }}
            />
            <div
              className="skeleton"
              style={{ height: 16, width: "60%", marginBottom: 16 }}
            />
            <div className="skeleton" style={{ height: 40, width: "100%" }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="masterclasses-page">
      <div className="masterclass-info-banner">
        <Award size={20} />
        <span>Select one masterclass per round. Seats are limited.</span>
      </div>

      {roundTabs.length > 1 && (
        <div className="round-tabs">
          {roundTabs.map((tab) => (
            <button
              key={tab.id}
              className={`round-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="round-label">{tab.label}</span>
              <span className="round-time">{tab.time}</span>
            </button>
          ))}
        </div>
      )}

      <div className="masterclasses-list">
        {currentRoundClasses.length > 0 ? (
          currentRoundClasses.map((mc) => {
            const availability = getAvailability(mc);
            const registered = isRegistered(mc.id);
            return (
              <div key={mc.id} className="masterclass-card">
                <div className="mc-header">
                  <div className="mc-content-summary">
                    <h3 className="mc-title">{mc.title}</h3>

                    <div
                      className="mc-subtitle"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        className="instructor-avatar-sm"
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          background: "var(--gradient-primary)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          fontWeight: "600",
                          flexShrink: 0,
                        }}
                      >
                        {mc.instructors && mc.instructors.length > 0 ? mc.instructors[0].name.charAt(0) : "T"}
                      </div>
                      <span>
                        {getInstructorNames(mc.instructors)}
                        {getInstructorTitles(mc) && ` • ${getInstructorTitles(mc)}`}
                      </span>
                    </div>

                    <div className="mc-schedule">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginTop: "4px",
                        }}
                      >
                        <MapPin size={14} className="text-muted" />
                        <span>{mc.location}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mc-details-block" style={{ marginTop: "12px" }}>
                  {mc.description && (
                    <div
                      className="mc-description"
                      style={{
                        marginBottom: "16px",
                        color: "var(--rf-gray-700)",
                        fontSize: "0.9375rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {mc.description}
                    </div>
                  )}

                  {mc.prerequisites && mc.prerequisites !== "None" && (
                    <div className="mc-meta-block">
                      <span className="meta-label">Prerequisites:</span>
                      <span className="meta-value">{mc.prerequisites}</span>
                    </div>
                  )}
                  {mc.materials && mc.materials !== "None" && (
                    <div className="mc-meta-block">
                      <span className="meta-label">Materials:</span>
                      <span className="meta-value">{mc.materials}</span>
                    </div>
                  )}

                  <div className="mc-action" style={{ marginTop: "20px" }}>
                    {registered ? (
                      <button
                        className="btn btn-success btn-sm"
                        disabled
                        style={{ opacity: 1, cursor: "default" }}
                      >
                        <CheckCircle size={16} />
                        Registered
                      </button>
                    ) : availability.status === "full" ? (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={(e) => handleRegisterClick(e, mc)}
                      >
                        Join Waitlist
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => handleRegisterClick(e, mc)}
                        disabled={registeringId === mc.id}
                      >
                        {registeringId === mc.id
                          ? "Registering..."
                          : "Register"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: "32px",
              textAlign: "center",
              color: "var(--rf-gray-500)",
            }}
          >
            No masterclasses found for this round.
          </div>
        )}
      </div>

      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title="Registration Issue"
      >
        <div style={{ padding: "10px 0", color: "var(--rf-gray-700)" }}>
          {errorModal.message}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setErrorModal({ ...errorModal, isOpen: false })}
          >
            Okay
          </button>
        </div>
      </Modal>
    </div>
  );
};
export default Masterclasses;
